import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Loader2, MousePointerClick, UserPlus, Percent, Crown } from "lucide-react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

const RANGES = [7, 30, 90];
const day = (iso) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });

function Stat({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="mm-glow-card p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs text-slate-400"><Icon className="h-4 w-4 text-[#00C896]" /> {label}</div>
      <p className="font-num text-2xl font-extrabold mt-2">{value}</p>
      {sub && <p className="text-[11px] text-slate-500 mt-0.5">{sub}</p>}
    </div>
  );
}

export default function DemoResults() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/admin/demo-stats?days=${days}`).then(({ data }) => setData(data)).catch(() => toast.error("Couldn't load demo results"));
  }, [days]);

  return (
    <div className="space-y-5" data-testid="demo-results">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-400 max-w-lg">A sign-up counts when someone creates an account on the same browser within 30 days of trying the demo.</p>
        <div className="flex gap-1.5">
          {RANGES.map((r) => (
            <button key={r} data-testid={`demo-results-range-${r}`} onClick={() => setDays(r)}
              className={`rounded-full px-3 py-1 text-xs border transition-colors ${days === r ? "border-[#00C896]/40 bg-[#00C896]/15 text-[#4DE3BF]" : "border-white/10 text-slate-400 hover:text-white"}`}>Last {r} days</button>
          ))}
        </div>
      </div>

      {!data ? (
        <div className="h-48 flex items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#00C896]" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Stat testid="demo-stat-visits" icon={MousePointerClick} label="Demo visits" value={data.visits}
              sub={`${data.visitors} unique visitor${data.visitors === 1 ? "" : "s"} · ${data.by_source.link || 0} from link, ${data.by_source.button || 0} from button`} />
            <Stat testid="demo-stat-signups" icon={UserPlus} label="Sign-ups from demo" value={data.signups} sub={`${data.all_time_signups} all time`} />
            <Stat testid="demo-stat-conversion" icon={Percent} label="Conversion" value={`${data.conversion_pct}%`}
              sub={data.median_hours_to_signup != null ? `Typically signs up ${data.median_hours_to_signup < 1 ? "within an hour" : `after ${data.median_hours_to_signup} hours`}` : "Sign-ups ÷ unique visitors"} />
            <Stat testid="demo-stat-premium" icon={Crown} label="Went Premium" value={data.premium} sub="of those sign-ups" />
          </div>

          <div className="mm-glow-card p-5" data-testid="demo-results-chart">
            <p className="text-sm font-medium mb-3">Visits and sign-ups per day</p>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={data.series} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                  <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={day} tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} minTickGap={24} />
                  <YAxis allowDecimals={false} tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip labelFormatter={day} contentStyle={{ background: "#14181D", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }} />
                  <Legend wrapperStyle={{ fontSize: 12, color: "#94a3b8" }} />
                  <Bar dataKey="visits" name="Demo visits" fill="#00C896" fillOpacity={0.35} radius={[4, 4, 0, 0]} />
                  <Line dataKey="signups" name="Sign-ups" stroke="#4DE3BF" strokeWidth={2.5} dot={false} type="monotone" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="mm-glow-card p-5" data-testid="demo-results-recent">
            <p className="text-sm font-medium">Recent sign-ups from the demo</p>
            <div className="mt-3 divide-y divide-white/6">
              {data.recent.map((r, i) => (
                <div key={i} data-testid={`demo-recent-row-${i}`} className="py-2.5 flex items-center justify-between gap-3 text-sm">
                  <span className="truncate">{r.email}</span>
                  <span className="flex items-center gap-2 shrink-0 text-xs text-slate-400">
                    <span className="hidden sm:inline">{r.profile_type === "student" ? "Student" : "Professional"}</span>
                    {r.premium && <span className="rounded-full border border-[#00C896]/30 bg-[#00C896]/10 px-1.5 py-0.5 text-[10px] text-[#00C896]">Premium</span>}
                    {day(r.joined)}
                  </span>
                </div>
              ))}
              {data.recent.length === 0 && <p data-testid="demo-results-empty" className="py-6 text-center text-sm text-slate-500">No sign-ups from the demo in this period yet.</p>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
